import React, { useRef } from 'react';
import { Box, Typography, Button, Chip, Alert } from '@mui/material';
import {
  UploadFile as UploadIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const ACCEPTED_TYPES = {
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'text/plain': '.txt',
};

const MAX_SIZE_MB = 10;

/**
 * Optional file upload section shown below the Scoping Notes textarea.
 * User can upload a PDF, DOCX, or TXT file as an alternative or supplement
 * to typing scoping notes manually.
 */
const FileUploadSection = ({ uploadedFile, uploadError, onFileSelect, onFileClear }) => {
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) validateAndSelect(file);
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) validateAndSelect(file);
    // reset so same file can be re-selected
    e.target.value = '';
  };

  const validateAndSelect = (file) => {
    if (!ACCEPTED_TYPES[file.type]) {
      onFileSelect(null, 'Unsupported file type. Please upload a PDF, DOCX, or TXT file.');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      onFileSelect(null, `File is too large. Maximum size is ${MAX_SIZE_MB} MB.`);
      return;
    }
    onFileSelect(file);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
          Upload Scoping Notes File{' '}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
            (Optional)
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload a PDF, DOCX, or TXT file as an alternative or supplement to the text above.
          Supported formats: <strong>.pdf</strong>, <strong>.docx</strong>, <strong>.txt</strong> — max {MAX_SIZE_MB} MB.
        </Typography>
      </Box>

      {uploadError && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }} onClose={onFileClear}>
          {uploadError}
        </Alert>
      )}

      {uploadedFile?.file ? (
        /* File selected — show preview chip */
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'success.light',
            background: 'rgba(180, 228, 206, 0.12)',
          }}
        >
          <FileIcon color="success" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {uploadedFile.file.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatSize(uploadedFile.file.size)} · Ready to upload
            </Typography>
          </Box>
          <Chip
            label="Remove"
            size="small"
            icon={<CloseIcon fontSize="small" />}
            onClick={onFileClear}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      ) : (
        /* Drop zone */
        <Box
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 3,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'rgba(89, 92, 234, 0.3)',
            background: 'rgba(89, 92, 234, 0.02)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: 'primary.main',
              background: 'rgba(89, 92, 234, 0.05)',
            },
          }}
        >
          <UploadIcon sx={{ fontSize: 36, color: 'rgba(89, 92, 234, 0.5)' }} />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Drag & drop a file here, or{' '}
            <Typography component="span" color="primary" fontWeight={600}>
              click to browse
            </Typography>
          </Typography>
          <Typography variant="caption" color="text.disabled">
            PDF · DOCX · TXT — max {MAX_SIZE_MB} MB
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            sx={{ mt: 0.5, borderRadius: 2, textTransform: 'none' }}
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          >
            Choose File
          </Button>
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,.txt"
        style={{ display: 'none' }}
        onChange={handleChange}
      />
    </Box>
  );
};

export default FileUploadSection;
